/**
 * Machine ID Attestation Service
 * 
 * Handles the complete attestation flow:
 * 1. Get challenge nonce from backend
 * 2. Request C++ to generate TPM signatures
 * 3. Send attestation to backend
 * 4. Store attestation metadata locally
 */

import constants from '@playcastdotio/GlobalConfig';
import * as Sentry from '@sentry/react';
import { ensureTrailingSlash } from '../modules/apiV2';
import { resolveCompatibleAttestationData } from './machineIdAttestationCompat';
import type { AttestationPayloadFields } from './machineIdAttestationCompat';

export { canonicalizeAttestationPayload } from './machineIdAttestationCompat';

/**
 * Challenge response from backend
 */
interface ChallengeResponse {
  nonce: string;
  expiresAt: number;
}

/**
 * Attestation payload (what gets signed by TPM)
 */
type AttestationPayload = AttestationPayloadFields;

/**
 * Attestation data from C++ (systemGetMachineIdAttestationMSG reply)
 * This is sent to the backend /attest endpoint
 * Note: clerkUserId is NOT sent in request body - backend extracts it from JWT token
 */
export interface AttestationData {
  machineId: string;
  aikPublicKey: string;
  seedSignature: string;
  attestationSignature: string;
  attestationPayload: AttestationPayload;
  /**
   * Wall-clock ms from the C++ reply. Not trusted for freshness — used only to
   * reconstruct the legacy signed payload when old natives still include
   * `timestamp` in the TPM signature (#14757 / #14485 rollout skew).
   */
  replyTimestamp?: number;
}

/**
 * Attestation result from backend
 */
interface AttestationResult {
  success: boolean;
  attestedAt?: number;
  expiresAt?: number;
  error?: string;
  message?: string;
}

/**
 * Attestation validation result (from isAttestationTentativelyValid)
 */
export interface AttestationValidation {
  isValid: boolean;
  attestedUserId?: string;
  attestedAt?: number;
  expiresAt?: number;
  reason?: 'EXPIRED' | 'USER_MISMATCH' | 'NOT_ATTESTED';
}

/**
 * Get a challenge nonce from the backend
 */
export async function getChallenge(): Promise<ChallengeResponse> {
  const url = `${ensureTrailingSlash(constants.REALTIME_API_BASE_URL)}realtime/machine-identity/challenge`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get challenge: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('[MachineIdAttestation] Challenge received:', {
      nonceLength: data.nonce?.length,
      expiresAt: data.expiresAt ? new Date(data.expiresAt).toISOString() : 'unknown',
    });

    return data;
  } catch (error) {
    console.error('[MachineIdAttestation] Failed to get challenge:', error);
    Sentry.captureException(error, {
      tags: {
        service: 'machineIdAttestation',
        operation: 'getChallenge',
      },
    });
    throw error;
  }
}

/**
 * Submit attestation to backend
 */
export async function submitAttestation(
  attestationData: AttestationData,
  clerkToken: string,
): Promise<AttestationResult> {
  const url = `${ensureTrailingSlash(constants.REALTIME_API_BASE_URL)}realtime/machine-identity/attest`;

  // Prefer modern 3-field payload; if local verify fails, retry with the C++
  // reply timestamp so old natives that still signed it can succeed (#14757).
  // Done before POST so we do not burn the single-use nonce on a bad body.
  const compatibleData = await resolveCompatibleAttestationData(attestationData);
  // Never send replyTimestamp to the API — it is client-only compatibility metadata.
  const { replyTimestamp: _replyTimestamp, ...requestPayload } = compatibleData;
  void _replyTimestamp;

  const requestBody = JSON.stringify(requestPayload);
  console.log('[ATTESTATION_DIAGNOSTIC] POST', url);
  console.log('[ATTESTATION_DIAGNOSTIC] body bytes:', requestBody.length);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${clerkToken}`,
      },
      body: requestBody,
    });

    // Check HTTP status before parsing response
    // The backend may return JSON even on error status codes, but we should validate
    if (!response.ok) {
      // Try to extract error message from response body if available
      // The backend typically returns JSON with error details even on HTTP errors
      let errorMessage = `Failed to submit attestation: ${response.status} ${response.statusText}`;
      let errorData: AttestationResult | null = null;
      
      try {
        const text = await response.text();
        if (text) {
          errorData = JSON.parse(text) as AttestationResult;
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        }
      } catch {
        // If JSON parsing fails, use the default error message
        // This can happen if backend returns non-JSON error (e.g., HTML error page)
      }
      
      // Throw error with details from JSON if available, or fallback to HTTP status
      const error = new Error(errorMessage);
      if (errorData) {
        // Include error details in the thrown error for better error handling
        (error as any).attestationResult = errorData;
      }
      throw error;
    }

    // Response is OK - safe to parse JSON
    const data = await response.json() as AttestationResult;

    if (!data.success) {
      console.warn('[MachineIdAttestation] Attestation failed:', data);
      Sentry.captureMessage('Machine ID attestation failed', {
        level: 'warning',
        tags: {
          service: 'machineIdAttestation',
          operation: 'submitAttestation',
          error: data.error,
        },
        contexts: {
          attestation: {
            error: data.error,
            message: data.message,
          },
        },
      });
    } else {
      const fmtDate = (v: number | undefined, fallback: string) => {
        if (!v) return fallback;
        try { const d = new Date(v); return isNaN(d.getTime()) ? fallback : d.toISOString(); } catch { return fallback; }
      };
      console.log('[MachineIdAttestation] Attestation successful:', {
        attestedAt: fmtDate(data.attestedAt, 'unknown'),
        expiresAt: fmtDate(data.expiresAt, 'never'),
      });
    }

    return data;
  } catch (error) {
    console.error('[MachineIdAttestation] Failed to submit attestation:', error);
    Sentry.captureException(error, {
      tags: {
        service: 'machineIdAttestation',
        operation: 'submitAttestation',
      },
    });
    throw error;
  }
}

/**
 * Complete attestation flow
 * 
 * @param clerkUserId - Clerk user ID
 * @param clerkToken - Clerk authentication token
 * @param getMachineIdAttestationFn - Function to call C++ to get attestation data
 * @returns Attestation result
 */
export async function performAttestation(
  clerkUserId: string,
  clerkToken: string,
  getMachineIdAttestationFn: (nonce: string, clerkUserId: string) => Promise<AttestationData>,
): Promise<AttestationResult> {
  try {
    console.log('[MachineIdAttestation] Starting attestation flow for user:', clerkUserId);

    // Step 1: Get challenge nonce from backend
    const challenge = await getChallenge();

    // Step 2: Call C++ to generate TPM signatures
    console.log('[MachineIdAttestation] Requesting attestation data from C++');
    const attestationData = await getMachineIdAttestationFn(challenge.nonce, clerkUserId);

    // Validate that the attestation data is complete
    if (
      !attestationData.machineId ||
      !attestationData.aikPublicKey ||
      !attestationData.seedSignature ||
      !attestationData.attestationSignature ||
      !attestationData.attestationPayload
    ) {
      throw new Error('Incomplete attestation data received from C++');
    }

    console.log('[MachineIdAttestation] Attestation data received from C++:', {
      machineId: attestationData.machineId.substring(0, 16) + '...',
      aikPublicKeyLength: attestationData.aikPublicKey.length,
      seedSignatureLength: attestationData.seedSignature.length,
      attestationSignatureLength: attestationData.attestationSignature.length,
    });

    // Step 3: Submit attestation to backend
    const result = await submitAttestation(attestationData, clerkToken);

    if (result.success) {
      console.log('[MachineIdAttestation] Attestation flow completed successfully');
      Sentry.addBreadcrumb({
        category: 'machineIdAttestation',
        message: 'Attestation successful',
        level: 'info',
        data: {
          clerkUserId,
          attestedAt: result.attestedAt,
        },
      });
    }

    return result;
  } catch (error) {
    console.error('[MachineIdAttestation] Attestation flow failed:', error);
    Sentry.captureException(error, {
      tags: {
        service: 'machineIdAttestation',
        operation: 'performAttestation',
      },
      contexts: {
        user: {
          clerkUserId,
        },
      },
    });
    throw error;
  }
}

/**
 * Check if we need to re-attest
 * 
 * This should be called after login to determine if attestation is needed.
 * The C++ code maintains attestation metadata in the registry.
 * 
 * @param clerkUserId - Current Clerk user ID
 * @param checkValidationFn - Function to call C++ to check attestation validity
 * @returns true if attestation is needed, false if already valid
 */
export async function needsAttestation(
  clerkUserId: string,
  checkValidationFn: (clerkUserId: string) => Promise<AttestationValidation>,
): Promise<boolean> {
  try {
    const validation = await checkValidationFn(clerkUserId);

    if (validation.isValid) {
      let attestedAtStr = 'unknown';
      try {
        if (validation.attestedAt) {
          const d = new Date(validation.attestedAt);
          if (!isNaN(d.getTime())) attestedAtStr = d.toISOString();
        }
      } catch { /* safe to ignore — only affects log output */ }

      console.log('[MachineIdAttestation] Existing attestation is valid:', {
        attestedUserId: validation.attestedUserId,
        attestedAt: attestedAtStr,
      });
      return false;
    }

    console.log('[MachineIdAttestation] Attestation needed:', validation.reason);
    return true;
  } catch (error) {
    console.error('[MachineIdAttestation] Failed to check attestation validity:', error);
    return true;
  }
}

